import type { Representada, SubPedido } from '../db/tipos'
import type { DadosPedidoPdf, DadosSubPedidoPdf } from './pdfDados'
import { letraDoIndice } from './numeracao'
import { arredondar2 } from './precos'

// A letra exibida e DERIVADA, nao a gravada. Desde que o sub-pedido virou
// "um por marca+tabela", a letra gravada identifica a tabela; o documento e
// o numero, porem, sao da MARCA. Ver a spec 2026-07-21-pdf-por-marca-design.
export function letrasPorMarca(subPedidos: SubPedido[]): Map<string, string> {
  const letras = new Map<string, string>()
  for (const subPedido of subPedidos) {
    if (letras.has(subPedido.representada_id)) continue
    letras.set(subPedido.representada_id, letraDoIndice(letras.size))
  }
  return letras
}

export interface MarcaDoPedido {
  representadaId: string
  representada: Representada
  letra: string
  numero: string
  tabelas: DadosSubPedidoPdf[]
  // Soma de TODAS as tabelas, canceladas inclusive. E o numero que o RISCADO
  // mostra quando a marca inteira cai: riscar um zero nao diria quanto caiu.
  total: number
  // Soma so das tabelas que seguem de pe -- o que o cliente vai mesmo pagar.
  // Numa marca MEIO cancelada os dois divergem, e e este que fecha com o
  // "TOTAL A PAGAR" do rodape. Somar tudo aqui fazia a folha se contradizer:
  // subtotal de 160 com 60 a pagar, sem nada explicando os 100 que faltavam.
  totalCobravel: number
  // Verdadeiro so quando TODAS as tabelas da marca cairam. Derivado, nunca
  // gravado.
  cancelada: boolean
}

export function agruparPorMarca(
  dados: DadosSubPedidoPdf[],
  numeroDoPedido: string,
): MarcaDoPedido[] {
  const letras = letrasPorMarca(dados.map((d) => d.subPedido))
  const porMarca = new Map<string, MarcaDoPedido>()
  for (const entrada of dados) {
    const representadaId = entrada.subPedido.representada_id
    const existente = porMarca.get(representadaId)
    const estaCancelada = entrada.subPedido.status === 'cancelado'
    if (existente) {
      existente.tabelas.push(entrada)
      existente.total = arredondar2(existente.total + entrada.subPedido.valor_total)
      if (!estaCancelada) {
        existente.totalCobravel = arredondar2(
          existente.totalCobravel + entrada.subPedido.valor_total,
        )
      }
      existente.cancelada = existente.cancelada && estaCancelada
      continue
    }
    const letra = letras.get(representadaId) ?? 'A'
    porMarca.set(representadaId, {
      representadaId,
      representada: entrada.representada,
      letra,
      numero: `${numeroDoPedido}-${letra}`,
      tabelas: [entrada],
      total: arredondar2(entrada.subPedido.valor_total),
      totalCobravel: estaCancelada ? 0 : arredondar2(entrada.subPedido.valor_total),
      cancelada: estaCancelada,
    })
  }
  return [...porMarca.values()]
}

export interface QuebraPorCondicao {
  rotuloCondicao: string | null
  condicoes: DadosSubPedidoPdf // representante do grupo, para desenhar o bloco
  itens: DadosSubPedidoPdf['itens']
  total: number
  // ⚠ NAO e o mesmo `cancelada` de MarcaDoPedido. Aqui significa "ESTE bloco
  // caiu"; la significa "TODAS as tabelas da marca cairam". Numa marca meio
  // cancelada este e true no bloco que caiu e o da marca continua false --
  // a marca segue sendo cobrada, so que por menos.
  cancelada: boolean
}

// A chave e a tupla INTEIRA das condicoes, nao so o pagamento: duas tabelas
// com o mesmo pagamento e fretes diferentes, fundidas, fariam o frete de uma
// delas sumir do PDF. Ver D4 da spec 2026-07-21-pdf-por-marca-design.
export function quebrasPorCondicao(marca: MarcaDoPedido): QuebraPorCondicao[] {
  const porChave = new Map<string, QuebraPorCondicao>()
  for (const entrada of marca.tabelas) {
    const { subPedido } = entrada
    const chave = JSON.stringify([
      entrada.condicaoPagamento,
      subPedido.frete_tipo,
      subPedido.transportadora,
      subPedido.previsao_entrega,
      // Cancelada nunca se funde com viva: os produtos dela apareceriam dentro
      // do bloco cobrado, sem tarja. O motivo tambem entra -- duas tabelas que
      // cairam por razoes diferentes contam historias diferentes, e o bloco so
      // imprime a do representante do grupo.
      subPedido.status === 'cancelado',
      subPedido.motivo_cancelamento,
    ])
    const existente = porChave.get(chave)
    if (existente) {
      existente.itens = [...existente.itens, ...entrada.itens]
      existente.total = arredondar2(existente.total + subPedido.valor_total)
      continue
    }
    porChave.set(chave, {
      rotuloCondicao: entrada.condicaoPagamento,
      condicoes: entrada,
      itens: [...entrada.itens],
      total: arredondar2(subPedido.valor_total),
      cancelada: subPedido.status === 'cancelado',
    })
  }
  return [...porChave.values()]
}

// As DUAS portas de leitura das marcas, e as unicas. Nenhuma tela filtra por
// conta propria: EnviarPedido desenha os botoes por indice e DocumentoSubPedido
// acha a marca pelo MESMO indice -- filtros diferentes nos dois lados fariam o
// botao de uma marca gerar o PDF de outra.
export function marcasParaCliente(dados: DadosPedidoPdf): MarcaDoPedido[] {
  return agruparPorMarca(dados.subPedidos, dados.pedido.numero)
}

// ⚠ Agrupa PRIMEIRO e filtra DEPOIS, de proposito. As letras saem da ordem da
// lista (letrasPorMarca), entao filtrar antes faria a marca viva mudar de letra
// quando a anterior cai -- o mesmo pedido com dois numeros em dois documentos.
//
// Descarta TABELA por tabela, nao a marca inteira: uma marca pode ter varias
// tabelas e nada obriga que caiam juntas. Deixar a marca passar inteira faria o
// documento da fabrica imprimir a tabela cancelada junto, que e exatamente o que
// o filtro antigo de pdfDados impedia.
export function marcasParaFabrica(dados: DadosPedidoPdf): MarcaDoPedido[] {
  return marcasParaCliente(dados)
    .map((marca) => {
      const tabelas = marca.tabelas.filter(
        (t: DadosSubPedidoPdf) => t.subPedido.status !== 'cancelado',
      )
      const total = arredondar2(
        tabelas.reduce((soma, t) => soma + t.subPedido.valor_total, 0),
      )
      return {
        ...marca,
        tabelas,
        total,
        // Nesta leitura nao existe tabela cancelada, entao os dois totais tem
        // de ser o MESMO numero. Herdar o totalCobravel da leitura do cliente
        // funciona hoje por coincidencia; escrever calado deixaria o rodape da
        // fabrica dependendo dela.
        totalCobravel: total,
        // Recalculado, nao herdado: o filter abaixo derruba a marca que ficou
        // sem tabela, entao tudo que sai daqui tem ao menos uma tabela viva --
        // isto e, nunca esta cancelada. Sem esta linha o raciocinio so existia
        // fora do codigo.
        cancelada: false,
      }
    })
    .filter((marca) => marca.tabelas.length > 0)
}
